import { useMemo } from "react";
import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

/** Negative delay (ms) that aligns an infinite animation to a global clock. */
function syncDelay(durationMs: number): number {
  const elapsed = typeof performance !== "undefined" ? performance.now() : 0;
  return -(elapsed % durationMs);
}

const sizeConfig = {
  sm: { wh: "w-4 h-4", thickness: 2 },
  md: { wh: "w-8 h-8", thickness: 3 },
  lg: { wh: "w-12 h-12", thickness: 4 },
  xl: { wh: "w-16 h-16", thickness: 4 },
  "2xl": { wh: "w-20 h-20", thickness: 5 },
} as const;

const spinnerVariants = cva("relative", {
  variants: {
    size: {
      sm: sizeConfig.sm.wh,
      md: sizeConfig.md.wh,
      lg: sizeConfig.lg.wh,
      xl: sizeConfig.xl.wh,
      "2xl": sizeConfig["2xl"].wh,
    },
  },
  defaultVariants: {
    size: "md",
  },
});

interface SpinnerProps extends VariantProps<typeof spinnerVariants> {
  className?: string;
}

export function Spinner({ size, className }: SpinnerProps) {
  const resolved = size ?? "md";
  const { thickness } = sizeConfig[resolved];
  const spinDelay = useMemo(() => syncDelay(900), []);
  const pulseDelay = useMemo(() => syncDelay(2000), []);

  // Mask cuts a hole in the center to create a ring from the conic-gradient
  const maskImage = `radial-gradient(farthest-side, transparent calc(100% - ${thickness}px - 0.5px), #000 calc(100% - ${thickness}px))`;

  return (
    <div className={cn(spinnerVariants({ size }), className)}>
      {/* Layer 1: Track ring — faint purple ring showing full circle path */}
      <div
        className="absolute inset-0 rounded-full border border-brand/20"
        style={{ borderWidth: thickness }}
      />

      {/* Layer 2: Gradient arc — spinning conic gradient masked into a ring */}
      <div
        className="absolute inset-0 rounded-full animate-spin"
        style={{
          background:
            "conic-gradient(from 0deg, transparent 0%, hsl(var(--brand)) 30%, hsl(var(--brand-secondary)) 70%, transparent 100%)",
          WebkitMaskImage: maskImage,
          maskImage,
          animationDuration: "0.9s",
          animationTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          animationDelay: `${spinDelay}ms`,
        }}
      />

      {/* Layer 3: Subtle pulsing glow */}
      <div
        className="absolute inset-0 rounded-full animate-pulse"
        style={{
          boxShadow: "0 0 8px 2px hsl(var(--brand) / 0.15)",
          animationDuration: "2s",
          animationDelay: `${pulseDelay}ms`,
        }}
      />
    </div>
  );
}
